import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FiChevronRight, FiMenu, FiPhone, FiX } from "react-icons/fi";
import { useHomePage } from "../context/HomePageContext";
import {
  logo,
  heroBg,
  innerBannerBg,
} from "../assets/appImages";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Awards", to: "/awards" },
  { label: "Blog & News", to: "/blog" },
  { label: "Contact Us", to: "/contact-us" },
];

const isSvgUrl = (url = "") => /\.svg($|\?)/i.test(url);

const HeroBannerImage = ({ src, alt, className }) => {
  const [imageSrc, setImageSrc] = useState("");

  useEffect(() => {
    let objectUrl = "";
    let isMounted = true;

    const loadImage = async () => {
      if (!src) {
        if (isMounted) setImageSrc("");
        return;
      }

      if (isSvgUrl(src)) {
        try {
          const response = await fetch(src);
          if (!response.ok) throw new Error("Failed to load banner image");
          const svgText = await response.text();
          const blob = new Blob([svgText], { type: "image/svg+xml" });
          objectUrl = URL.createObjectURL(blob);
          if (isMounted) setImageSrc(objectUrl);
          return;
        } catch {
          if (isMounted) setImageSrc("");
          return;
        }
      }

      if (isMounted) setImageSrc(src);
    };

    loadImage();

    return () => {
      isMounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (!imageSrc) return null;

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
      referrerPolicy="no-referrer"
      onError={() => setImageSrc("")}
    />
  );
};

const DEFAULT_PHONE_NUMBER = "(+91) 9650193020";
const DEFAULT_PHONE_HREF = "tel:+919650193020";

const formatPhoneHref = (phone = "") => {
  const digits = phone.replace(/\D/g, "");
  return digits ? `tel:+${digits}` : DEFAULT_PHONE_HREF;
};

const formatPhoneDisplay = (phone = "") => phone.trim() || DEFAULT_PHONE_NUMBER;

const BookAppointmentButton = ({ className = "", onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-3 rounded-lg bg-brand-purple px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-purple-dark ${className}`}
  >
    <span>Book Appointment</span>
    <span className="flex h-7 w-7 items-center justify-center rounded-md bg-white text-brand-purple">
      <FiChevronRight className="text-lg" />
    </span>
  </button>
);

const ConsultationButton = ({ className = "", onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-block rounded-lg bg-brand-orange px-6 py-3 text-sm font-semibold text-white shadow-cta-orange transition-transform hover:scale-[1.02] md:px-8 md:py-3.5 md:text-base ${className}`}
  >
    Book Your Consultation Today
  </button>
);

const HeroSlideBackground = ({ children, className = "", contentClassName = "" }) => (
  <div
    className={`relative h-[calc(100svh-76px)] max-h-[540px] min-h-[420px] w-full overflow-hidden bg-[#F7F8FC] md:h-[calc(100svh-84px)] md:max-h-[580px] ${className}`}
  >
    <img
      src={heroBg}
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full object-fill"
    />
    <div className={`relative z-[1] h-full w-full ${contentClassName}`}>{children}</div>
  </div>
);

const HeroContentSlide = ({ slide, onOpenBooking }) => (
  <HeroSlideBackground>
    <div className="relative mx-auto flex h-full max-w-7xl flex-col items-center gap-4 px-4 py-4 md:px-8 lg:flex-row lg:justify-between lg:gap-6 lg:py-6">
      <div className="w-full max-w-2xl text-left lg:w-[48%]">
        <p className="text-sm font-medium text-brand-purple md:text-base">
          {slide.subtitle}
        </p>
        <h1 className="mt-2 text-2xl font-bold leading-tight text-brand-purple md:text-3xl lg:text-[2.25rem] lg:leading-[1.15]">
          {slide.title}
        </h1>
        <div className="mt-5">
          <ConsultationButton onClick={onOpenBooking} />
        </div>
      </div>

      <div className="relative flex h-full min-h-0 w-full flex-1 items-end justify-center lg:w-[52%] lg:justify-center">
        <HeroBannerImage
          src={slide.image}
          alt={slide.title}
          className="h-auto w-auto max-h-[400px] max-w-full object-cover object-top sm:max-h-[260px] md:max-h-[450px] lg:max-h-[500px] lg:max-w-[90%]"
        />
      </div>
    </div>
  </HeroSlideBackground>
);

const AwardsSlide = ({ image }) => (
  <HeroSlideBackground contentClassName="flex items-center justify-center">
    <div className="mx-auto w-full max-w-8xl px-2 py-2 md:px-4">
      <HeroBannerImage
        src={image}
        alt="Awards and honors"
        className="mx-auto h-auto max-h-[280px] w-full object-contain md:max-h-[340px]"
      />
    </div>
  </HeroSlideBackground>
);

const Header = ({ scrollToContact, onOpenBooking }) => {
  const { banners, contact } = useHomePage();
  const [isSticky, setIsSticky] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const bannerRef = useRef(null);
  const location = useLocation();
  const isHome = location.pathname === "/";

  const heroSlides = useMemo(
    () =>
      banners.map((slide) => ({
        ...slide,
        cta: "Book Your Consultation Today",
      })),
    [banners]
  );

  const phoneNumber = formatPhoneDisplay(contact?.phone_number);
  const phoneHref = formatPhoneHref(contact?.phone_number);
  // API logo is SVG (octet-stream/attachment) — browser <img> can't render it reliably
  const headerLogo =
    contact?.logo && !/\.svg($|\?)/i.test(contact.logo) ? contact.logo : logo;

  useLayoutEffect(() => {
    const handleScroll = () => setIsSticky(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isHome || heroSlides.length <= 1) return undefined;

    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [isHome, heroSlides.length]);

  useEffect(() => {
    setActiveSlide(0);
  }, [heroSlides.length]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const path = location.pathname;

  let pageTitle = "Expert Orthopaedics & Spine Surgeon";
  let pageSubtitle = "Dr. Yash Gulati — Advanced Orthopaedics & Spine Care";
  let bannerHeight = "h-[320px] md:h-[400px]";
  let showBanner = !path.startsWith("/courses/");

  if (path === "/about") {
    pageTitle = "About";
    pageSubtitle = "";
    bannerHeight = "h-[260px] md:h-[300px]";
  } else if (path === "/services") {
    pageTitle = "Services";
    pageSubtitle = "";
    bannerHeight = "h-[260px] md:h-[300px]";
  } else if (path.startsWith("/services/")) {
    pageTitle = "Service Detail";
    pageSubtitle = "";
    bannerHeight = "h-[260px] md:h-[300px]";
  } else if (path === "/awards") {
    pageTitle = "Awards";
    pageSubtitle = "";
    bannerHeight = "h-[260px] md:h-[300px]";
  } else if (path === "/blog" || path.startsWith("/blog/")) {
    pageTitle = path === "/blog" ? "Blogs" : "Blog Details";
    pageSubtitle = "";
    bannerHeight = "h-[260px] md:h-[300px]";
  } else if (path === "/contact-us") {
    pageTitle = "Contact Us";
    pageSubtitle = "";
    bannerHeight = "h-[260px] md:h-[300px]";
  } else if (path.startsWith("/courses/")) {
    showBanner = false;
  }

  const currentSlide = heroSlides[activeSlide] || heroSlides[0];

  return (
    <div className="relative">
      <header
        className={`z-50 w-full transition-all duration-300 ${
          isSticky || !isHome
            ? "fixed top-0 left-0 right-0 bg-white shadow-md"
            : "absolute top-0 left-0 right-0 bg-white"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-6 md:py-4">
          <Link to="/" className="flex shrink-0 items-center gap-3">
            <img
              src={headerLogo}
              alt="Dr Yash Gulati"
              className="h-14 w-14 object-contain md:h-[50px] md:w-[200px]"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = logo;
              }}
            />
          </Link>

          <nav className="hidden items-center gap-6 xl:flex">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-base font-semibold transition-colors hover:text-brand-purple-dark ${
                  location.pathname === link.to ||
                  (link.to === "/services" && location.pathname.startsWith("/services/")) ||
                  (link.to === "/blog" && location.pathname.startsWith("/blog/"))
                    ? "text-brand-purple"
                    : "text-brand-purple/90"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden items-center gap-4 lg:flex">
            <a href={phoneHref} className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-purple/10 text-brand-purple">
                <FiPhone className="text-base" />
              </span>
              <span className="whitespace-nowrap text-sm font-bold text-brand-purple md:text-base">
                {phoneNumber}
              </span>
            </a>
            <BookAppointmentButton onClick={onOpenBooking} />
          </div>

          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-lg text-brand-purple xl:hidden"
            onClick={() => setMobileOpen((open) => !open)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <FiX className="text-2xl" /> : <FiMenu className="text-2xl" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="border-t border-gray-100 bg-white px-4 py-4 xl:hidden">
            <nav className="flex flex-col gap-3">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-base font-medium text-brand-purple"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            <a
              href={phoneHref}
              className="mt-4 flex items-center gap-2 text-sm font-bold text-brand-purple"
            >
              <FiPhone />
              {phoneNumber}
            </a>
            <BookAppointmentButton className="mt-4 w-full justify-between" onClick={onOpenBooking} />
          </div>
        )}
      </header>

      {showBanner && (
        <div ref={bannerRef} className={isHome ? "pt-[76px] md:pt-[84px]" : "pt-[76px] md:pt-[84px]"}>
          {isHome ? (
            <section className="relative">
              {heroSlides.length > 0 && currentSlide ? (
                <>
                  {currentSlide.type === "content" && (
                    <HeroContentSlide slide={currentSlide} onOpenBooking={onOpenBooking} />
                  )}
                  {currentSlide.type === "awards" && (
                    <AwardsSlide image={currentSlide.image} />
                  )}

                  <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2">
                    {heroSlides.map((slide, index) => (
                      <button
                        key={slide.id}
                        type="button"
                        aria-label={`Go to slide ${index + 1}`}
                        onClick={() => setActiveSlide(index)}
                        className={`h-2.5 rounded-full transition-all ${
                          activeSlide === index
                            ? "w-8 bg-brand-purple"
                            : "w-2.5 bg-brand-purple/35"
                        }`}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <HeroSlideBackground />
              )}
            </section>
          ) : path === "/services" ||
            path.startsWith("/services/") ||
            path === "/about" ||
            path === "/awards" ||
            path === "/blog" ||
            path.startsWith("/blog/") ||
            path === "/contact-us" ? (
            <div
              className={`relative flex items-center justify-center overflow-hidden bg-[#F7F8FC] ${bannerHeight}`}
            >
              <img
                src={innerBannerBg}
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 h-full w-full object-fill"
              />
              <h1 className="relative z-[1] text-4xl font-bold text-brand-purple md:text-5xl lg:text-[3.5rem] lg:tracking-wide">
                {pageTitle}
              </h1>
            </div>
          ) : (
            <div
              className={`relative flex items-center justify-center bg-gradient-to-br from-brand-purple/10 to-white ${bannerHeight}`}
            >
              <div className="px-4 text-center">
                {pageSubtitle && (
                  <p className="text-sm font-medium text-brand-purple md:text-base">{pageSubtitle}</p>
                )}
                <h1
                  className={`font-bold text-brand-purple md:text-5xl ${
                    pageSubtitle ? "mt-3 text-3xl" : "text-3xl"
                  }`}
                >
                  {pageTitle}
                </h1>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Header;
